import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { InputOTP, InputOTPGroup, InputOTPSeparator, InputOTPSlot } from "@/components/ui/input-otp";
import { Progress } from "@/components/ui/progress";
import { type AuthLoadingStateWithProgress, useAuth } from "@/hooks/use-auth";
import { useGetLoggedInUser } from "@/hooks/use-get-logged-in-user";
import { useScopedI18n } from "@/locales/client";
import { REGEXP_ONLY_DIGITS } from "input-otp";
import { ChevronRight, Loader2, Mail, Wallet } from "lucide-react";
import { useState } from "react";
import { z } from "zod";
import { usePrefetchNonce } from "./providers/auth-provider";
import { SignInWithEthereumMenu } from "./signup-sidebar.content.menu";

export function SignUpSidebarContent({
    relativeRedirectUrl,
    sidebarType,
    onSidebarTypeChange,
}: {
    relativeRedirectUrl?: string;
    sidebarType: "signup" | "login";
    onSidebarTypeChange: (type: "signup" | "login") => void;
}) {
    const { reset, signIn, connectCoinbaseSmartWallet, signOut, loadingState } = useAuth();
    const { user } = useGetLoggedInUser();

    const isMagicAndLoggedIn = user?.category.startsWith("magic") ?? false;

    if (isMagicAndLoggedIn && loadingState.status === "idle") {
        return (
            <div className="h-full p-4">
                <div className="flex size-full flex-col items-center justify-center">
                    <ReconnectSlide
                        onReconnect={() => {
                            const currentEmail = user?.email;
                            if (currentEmail != null) {
                                void signIn({ connectorIndex: 0, email: currentEmail, relativeRedirectUrl });
                            }
                        }}
                        onUseDifferent={() => {
                            void signOut();
                        }}
                    />
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="h-full p-4">
                <div className="flex size-full flex-col items-center justify-center">
                    {loadingState.status === "idle" && (
                        <MainSlide
                            onReset={reset}
                            onSignIn={(v) => {
                                return signIn({ ...v, relativeRedirectUrl });
                            }}
                            onSmartWalletConnect={() => {
                                connectCoinbaseSmartWallet({
                                    relativeRedirectUrl,
                                    mode: sidebarType === "signup" ? "create" : "connect",
                                });
                            }}
                            sidebarType={sidebarType}
                            onSidebarTypeChange={onSidebarTypeChange}
                        />
                    )}
                    {loadingState.status === "otpEntering" && (
                        <OtpInputSlide
                            onOtpTokenEnter={loadingState.onOtpTokenEntered}
                            email={loadingState.email}
                            isRetry={loadingState.isRetry}
                        />
                    )}
                    {loadingState.status !== "idle" && loadingState.status !== "otpEntering" && (
                        <div className="flex h-full w-full items-center">
                            <Loading loadingState={loadingState} />
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}

function ReconnectSlide({ onReconnect, onUseDifferent }: { onReconnect: () => void; onUseDifferent: () => void }) {
    const { user } = useGetLoggedInUser();
    const t = useScopedI18n("signUpSidebar");

    return (
        <div className="flex flex-col items-center justify-center">
            <h3 className="font-bold text-3xl text-foreground">{t("walletConfirm.title")}</h3>
            <div className="my-4 text-center">
                {t("walletConfirm.description")}{" "}
                <span className="font-bold">
                    {user?.username} ({user?.email})
                </span>
                <div className="mt-8 flex w-full justify-center gap-3">
                    <Button onClick={onUseDifferent} variant="outline">
                        {t("walletConfirm.useDifferent")}
                    </Button>
                    <Button onClick={onReconnect}>{t("walletConfirm.confirm")}</Button>
                </div>
            </div>
        </div>
    );
}

function MainSlide({
    onSignIn,
    onSmartWalletConnect,
    onReset,
    sidebarType,
    onSidebarTypeChange,
}: {
    onSignIn: (result: { connectorIndex: number; email?: string }) => Promise<void>;
    onSmartWalletConnect: () => void;
    onReset: () => void;
    sidebarType: "signup" | "login";
    onSidebarTypeChange: (type: "signup" | "login") => void;
}) {
    const [email, setEmail] = useState("");
    const [emailError, setEmailError] = useState<string | undefined>(undefined);
    const t = useScopedI18n("signUpSidebar");

    usePrefetchNonce();

    async function signInWithMagic(): Promise<void> {
        onReset();
        setEmailError(undefined);

        const isEmailValid = z.string().email().safeParse(email).success;
        if (!isEmailValid) {
            setEmailError(t("emailLogin.errorInvalidEmail"));
            return;
        }

        await onSignIn({ connectorIndex: 0, email });
    }

    async function signInWithWallet(connectorIndex: number) {
        await onSignIn({ connectorIndex });
    }

    return (
        <div className="w-full">
            <h3 className="font-bold text-3xl">{t("title")}</h3>
            <Accordion type="single" collapsible className="w-full" defaultValue="wallet">
                <AccordionItem value="wallet">
                    <AccordionTrigger>
                        {sidebarType === "signup" ? t("smartWallet.title") : t("smartWallet.titleConnect")}
                    </AccordionTrigger>
                    <AccordionContent>
                        <p className="mb-5 text-muted-foreground text-xs">{t("smartWallet.description")}</p>
                        <Button onClick={onSmartWalletConnect} className="w-full" name="Connect Wallet">
                            <svg
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                xmlns="http://www.w3.org/2000/svg"
                                className="text-black dark:text-[#0000ff]"
                            >
                                <title>Base</title>
                                <path
                                    fill="currentColor"
                                    d="M0 2.014C0 1.58105 0 1.36457 0.0815779 1.19805C0.159686 1.03861 0.288611 0.909686 0.448049 0.831578C0.61457 0.75 0.831047 0.75 1.264 0.75H14.736C15.169 0.75 15.3854 0.75 15.552 0.831578C15.7114 0.909686 15.8403 1.03861 15.9184 1.19805C16 1.36457 16 1.58105 16 2.014V15.486C16 15.919 16 16.1354 15.9184 16.302C15.8403 16.4614 15.7114 16.5903 15.552 16.6684C15.3854 16.75 15.169 16.75 14.736 16.75H1.264C0.831047 16.75 0.61457 16.75 0.448049 16.6684C0.288611 16.5903 0.159686 16.4614 0.0815779 16.302C0 16.1354 0 15.919 0 15.486V2.014Z"
                                />
                            </svg>
                            {sidebarType === "signup" ? t("smartWallet.buttonCreate") : t("smartWallet.buttonConnect")}
                        </Button>
                    </AccordionContent>
                </AccordionItem>
                <AccordionItem value="email">
                    <AccordionTrigger>
                        {sidebarType === "signup" ? t("emailLogin.titleSignup") : t("emailLogin.titleLogin")}
                    </AccordionTrigger>
                    <AccordionContent>
                        <p className="mb-3 text-muted-foreground text-xs">{t("emailLogin.description")}</p>
                        <form
                            onSubmit={(e) => {
                                e.preventDefault();
                                void signInWithMagic();
                            }}
                        >
                            <>
                                <div className="m-0.5">
                                    <Input
                                        type="email"
                                        name="email"
                                        placeholder={t("emailLogin.placeholder")}
                                        className={`${emailError != null ? "border-destructive" : ""}`}
                                        onChange={(e) => {
                                            setEmail(e.target.value);
                                        }}
                                    />
                                </div>
                                {emailError != null && <div className="text-error">{emailError}</div>}

                                <div className="flex">
                                    <Button className="my-4 grow" type="submit">
                                        {sidebarType === "signup"
                                            ? t("emailLogin.buttonSignup")
                                            : t("emailLogin.buttonLogin")}
                                    </Button>
                                </div>
                            </>
                        </form>
                    </AccordionContent>
                </AccordionItem>
                <AccordionItem value="browser-wallet">
                    <AccordionTrigger>{t("browserWallet.title")}</AccordionTrigger>
                    <AccordionContent>
                        <p className="mb-3 text-muted-foreground text-xs">{t("browserWallet.description")}</p>
                        <SignInWithEthereumMenu signIn={signInWithWallet} reset={onReset} />
                    </AccordionContent>
                </AccordionItem>
            </Accordion>
            <hr className="border-b border-none" />
            <div className="my-4 flex items-center justify-between">
                <p className="text-muted-foreground text-xs">
                    {sidebarType === "signup" ? t("accountSwitch.existingUser") : t("accountSwitch.newUser")}
                </p>
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                        e.preventDefault();
                        onSidebarTypeChange(sidebarType === "signup" ? "login" : "signup");
                    }}
                >
                    {sidebarType === "signup" ? t("accountSwitch.logIn") : t("accountSwitch.signUp")}
                    <ChevronRight className="size-4" />
                </Button>
            </div>
        </div>
    );
}

function OtpInputSlide({
    onOtpTokenEnter,
    email,
    isRetry,
}: {
    onOtpTokenEnter: (token: string) => void;
    email: string;
    isRetry: boolean;
}) {
    const [input, setInput] = useState("");
    const t = useScopedI18n("signUpSidebar");

    return (
        <div className="flex w-full flex-col items-center gap-4">
            {isRetry ? (
                <>
                    <div className="text-error">{t("otpVerification.retry")}</div>
                    <div>{t("otpVerification.retryMessage")}</div>
                </>
            ) : (
                <div>{t("otpVerification.title")}</div>
            )}
            <div className="mb-6 font-bold text-xl">{email}</div>

            <InputOTP
                maxLength={6}
                pattern={REGEXP_ONLY_DIGITS}
                value={input}
                onChange={(v) => {
                    setInput(v);
                    if (v.length === 6) {
                        onOtpTokenEnter(v);
                    }
                }}
            >
                <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                </InputOTPGroup>
                <InputOTPSeparator />
                <InputOTPGroup>
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                </InputOTPGroup>
            </InputOTP>
        </div>
    );
}

function Loading({ loadingState }: { loadingState: AuthLoadingStateWithProgress }) {
    const t = useScopedI18n("signUpSidebar");

    // This screen should never show when not loading.
    if (loadingState.status === "idle" || loadingState.status === "otpEntering") {
        return null;
    }

    function getTitle() {
        switch (loadingState.status) {
            case "connecting":
                return t("loading.wait");
            case "waitingForWalletToConnect":
                return loadingState.walletID === "magic" ? t("loading.wait") : t("loading.connectWallet");
            case "waitForMessageToBeSigned": {
                return loadingState.walletID === "magic" ? t("loading.wait") : t("loading.signMessage");
            }
            default: {
                return t("loading.wait");
            }
        }
    }

    function getMessage() {
        switch (loadingState.status) {
            case "connecting":
                return t("loading.loadingWallet");
            case "waitingForWalletToConnect":
                return loadingState.walletID === "magic" ? (
                    t("loading.connectingWallet")
                ) : (
                    <>
                        {t("loading.connectWith")} <b>{Wallets[loadingState.walletID]}</b> wallet.
                    </>
                );
            case "requestMessageToSign":
                return t("loading.requestSign");
            case "waitForMessageToBeSigned":
                return t("loading.waitSign");
            case "verifySignature":
                return t("loading.verifySign");
            case "loadProfile":
                return t("loading.loadProfile");
        }
    }

    return (
        <div className="w-full p-4">
            <header className="flex items-center gap-2">
                {loadingState.walletID === "magic" ? <Mail /> : <Wallet />}
                <h4 className="text-foreground">{getTitle()}</h4>
            </header>
            <div className="my-4 flex gap-1 text-muted-foreground text-xs">
                <Loader2 className="size-4 animate-spin" />
                {getMessage()}
            </div>
            <Progress value={loadingState.progress} />
        </div>
    );
}

const Wallets: Record<string, string> = {
    coinbaseWallet: "Coinbase",
    metaMask: "MetaMask",
    walletConnect: "WalletConnect",
    magic: "Magic",
};
